import { ArrowRight, Brain, CheckCircle2, Database, FileAudio, Radio, Server, Sparkles, Trophy } from 'lucide-react'
import { useState } from 'react'

const PROGRESS_STEPS = [
  { percent: 10, label: '音声ファイル受信完了', icon: Radio, desc: 'サーバーに到達しました' },
  { percent: 20, label: 'ファイル保存完了', icon: Database, desc: '一時ストレージに保存しました' },
  { percent: 30, label: '音声変換中...', icon: FileAudio, desc: 'webm形式をwavに変換しています' },
  { percent: 50, label: '音声変換完了', icon: CheckCircle2, desc: '解析準備が整いました' },
  { percent: 60, label: '音声評価中', icon: Server, desc: '発音・流暢性をスコアリング中...' },
  { percent: 80, label: 'AI詳細分析中', icon: Brain, desc: 'AIがフィードバックを生成しています...' },
  { percent: 100, label: '完了・結果送信', icon: Sparkles, desc: 'レポートを作成しました' },
]

const getCurrentStepInfo = (progress: number) => {
  return (
    PROGRESS_STEPS.slice()
      .reverse()
      .find((s) => s.percent <= progress) || PROGRESS_STEPS[0]
  )
}

type FluidAiLoaderProps = {
  progress: number
  onComplete?: () => void
}

export const FluidAiLoader = ({ progress, onComplete }: FluidAiLoaderProps) => {
  const [isTransitioning, setIsTransitioning] = useState(false)
  // 完了時のキラキラ粒子 (マウント時に 1 回だけ Math.random で生成)
  const [sparkleParticles] = useState(() =>
    Array.from({ length: 12 }, (_, i) => ({
      width: Math.random() * 4 + 2,
      height: Math.random() * 4 + 2,
      top: 50 + (40 + Math.random() * 20) * Math.sin(i * ((Math.PI * 2) / 12)),
      left: 50 + (40 + Math.random() * 20) * Math.cos(i * ((Math.PI * 2) / 12)),
      delay: Math.random() * 2,
    })),
  )
  const currentInfo = getCurrentStepInfo(progress)
  const isCompleted = progress >= 100

  // 色の計算ロジック
  const startHue = 260 // Purple
  const endHue = 150 // Green
  const currentHue = startHue - (startHue - endHue) * (Math.min(progress, 100) / 100)

  // グラデーション: 完了時は「ゴールド/レインボー」で特別感を出す
  const dynamicGradient = isCompleted
    ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)' // Gold for Result
    : `linear-gradient(135deg,
        hsl(${currentHue}, 90%, 60%) 0%,
        hsl(${currentHue - 30}, 80%, 55%) 50%,
        hsl(${currentHue - 60}, 90%, 65%) 100%)`

  const handleShowResult = () => {
    setIsTransitioning(true)
    // アニメーション完了までの時間 (1.2s)
    setTimeout(() => {
      if (onComplete) onComplete()
    }, 1200)
  }

  return (
    <div className="flex flex-col items-center justify-center h-full w-full relative overflow-hidden animate-in fade-in duration-500">
      <style>{`
        @keyframes morph {
          0% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
          50% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
          100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse-ring {
          0% { transform: scale(0.8); opacity: 0.5; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        @keyframes sparkle {
          0%, 100% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1); opacity: 1; }
        }
        @keyframes explode-container {
          0% { transform: scale(1); opacity: 1; }
          40% { transform: scale(0.9); opacity: 1; }
          100% { transform: scale(50); opacity: 0; }
        }
        .fluid-blob {
          animation: morph 8s ease-in-out infinite, spin-slow 20s linear infinite;
          transition: background 0.5s ease, filter 0.5s ease;
        }
        .animate-explode {
          animation: explode-container 1.2s cubic-bezier(0.7, 0, 0.3, 1) forwards;
        }
      `}</style>

      <div className="relative w-64 h-64 flex items-center justify-center">
        {/* 完了時のパルスリング（衝撃波） */}
        {isCompleted && !isTransitioning && (
          <>
            <div className="absolute inset-0 border border-yellow-500/50 rounded-full animate-[pulse-ring_3s_infinite]" />
            <div
              className="absolute inset-4 border border-yellow-200/30 rounded-full animate-[pulse-ring_3s_infinite]"
              style={{ animationDelay: '1s' }}
            />
          </>
        )}

        {/* Main Fluid Blob Container (For Explosion) */}
        <div className={`absolute inset-0 z-50 ${isTransitioning ? 'animate-explode' : ''}`}>
          <div
            className={`fluid-blob absolute inset-0 ${isCompleted ? 'drop-shadow-[0_0_30px_rgba(251,191,36,0.8)]' : 'drop-shadow-[0_0_15px_rgba(99,102,241,0.5)]'}`}
            style={{
              background: dynamicGradient,
              filter: isCompleted ? 'blur(0px)' : 'blur(1px)',
              boxShadow: 'inset 0 0 20px rgba(255,255,255,0.5)',
            }}
          />
        </div>

        {/* 内部のアイコンとパーセンテージ */}
        <div
          className={`relative z-50 flex flex-col items-center justify-center text-white drop-shadow-md transition-all duration-300 ${isTransitioning ? 'opacity-0 scale-90' : 'opacity-100'}`}
        >
          {isCompleted ? (
            <div className="flex flex-col items-center animate-in zoom-in slide-in-from-bottom-4 duration-500">
              <div className="relative">
                <div className="absolute inset-0 bg-yellow-400 blur-xl opacity-50 animate-pulse" />
                <Trophy className="relative w-20 h-20 mb-2 text-yellow-100 drop-shadow-md" />
              </div>
              <span
                className="text-xl font-bold text-yellow-100 tracking-widest uppercase mt-2"
                style={{ textShadow: '0 0 20px rgba(255,255,255,0.5)' }}
              >
                Score Ready
              </span>
            </div>
          ) : (
            <>
              <span
                className="text-5xl font-black tracking-tighter tabular-nums"
                style={{ textShadow: '0 0 20px rgba(255,255,255,0.5)' }}
              >
                {Math.round(progress)}
                <span className="text-2xl text-white/50">%</span>
              </span>
              <currentInfo.icon className="w-8 h-8 mt-4 opacity-80 animate-pulse" />
            </>
          )}
        </div>

        {/* 周囲を飛ぶ粒子（完了時は増える） */}
        {!isTransitioning && (
          <div className="absolute inset-[-60px] pointer-events-none">
            {isCompleted ? (
              // 完了時のキラキラ
              <>
                {sparkleParticles.map((p, i) => (
                  <div
                    key={i}
                    className="absolute rounded-full animate-[sparkle_2s_infinite]"
                    style={{
                      width: p.width + 'px',
                      height: p.height + 'px',
                      backgroundColor: '#fef3c7',
                      top: `${p.top}%`,
                      left: `${p.left}%`,
                      animationDelay: `${p.delay}s`,
                      boxShadow: '0 0 10px white',
                    }}
                  />
                ))}
              </>
            ) : (
              // 解析中の粒子
              <>
                <div
                  className="absolute top-1/2 left-0 w-2 h-2 bg-indigo-200 rounded-full animate-ping"
                  style={{ animationDuration: '3s', boxShadow: '0 0 10px white' }}
                />
                <div
                  className="absolute bottom-0 right-1/4 w-1.5 h-1.5 bg-purple-300 rounded-full animate-ping"
                  style={{ animationDuration: '2s', animationDelay: '1s', boxShadow: '0 0 10px white' }}
                />
              </>
            )}
          </div>
        )}
      </div>

      {/* テキスト情報 & アクションボタン */}
      <div
        className={`mt-16 text-center space-y-6 z-20 max-w-xs px-4 min-h-[120px] flex flex-col items-center justify-center transition-all duration-700 ${isTransitioning ? 'translate-y-10 opacity-0' : 'translate-y-0 opacity-100'}`}
      >
        {isCompleted ? (
          <button
            onClick={handleShowResult}
            className="group relative overflow-hidden bg-linear-to-r from-yellow-100 to-yellow-50 text-yellow-900 font-black py-4 px-10 rounded-full shadow-[0_0_30px_rgba(234,179,8,0.4)] hover:shadow-[0_0_50px_rgba(234,179,8,0.6)] hover:scale-105 transition-all duration-300 flex items-center gap-3 border border-yellow-200"
          >
            <span className="relative z-10 text-lg">結果を見る</span>
            <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
            <div className="absolute inset-0 bg-white/50 transform -skew-x-12 -translate-x-full group-hover:animate-pulse" />
          </button>
        ) : (
          <div className="w-full">
            <h3
              className="text-2xl font-bold text-white tracking-tight"
              style={{ textShadow: '0 0 20px rgba(255,255,255,0.5)' }}
            >
              {currentInfo.label}
            </h3>
            <div className="mt-3 flex justify-center">
              <p className="text-sm font-medium text-slate-300 bg-white/10 px-4 py-1.5 rounded-full backdrop-blur-md border border-white/10 shadow-lg">
                {currentInfo.desc}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
